import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { LOGO_URL } from "@/lib/api";
import { Scale, FileSearch, MessageSquareText, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1576520043081-2247049740d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const features = [
  { icon: MessageSquareText, title: "Comprendre vos droits", text: "Posez votre question en langage courant. L'assistant vous explique la loi simplement, avec les articles applicables." },
  { icon: FileSearch, title: "Analyser vos documents", text: "Convocation, contrat, courrier ? Envoyez une photo et obtenez un résumé clair, les dates clés et les actions à mener." },
  { icon: Scale, title: "Recherche juridique", text: "Trouvez les textes de loi français pertinents pour votre situation, expliqués et sourcés." },
  { icon: ShieldCheck, title: "Préparer votre avocat", text: "Structurez votre dossier et arrivez préparé à votre consultation. Un assistant, jamais un substitut." },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-bone">
      {/* Logo watermark background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 flex items-center justify-center overflow-hidden">
        <img src={LOGO_URL} alt="" className="w-[min(90vw,820px)] max-w-none opacity-[0.04] select-none" />
      </div>
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Droits IA.com" className="h-9 w-9 object-contain" />
            <span className="font-serif text-2xl font-semibold text-navy">Droits IA.com</span>
          </div>
          <button data-testid="header-login-btn" onClick={handleLogin} className="text-sm font-medium text-navy hover:text-royal transition-colors flex items-center gap-2">
            Se connecter <ArrowRight size={15} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-bone/85" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-28 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-royal mb-6">
              <Sparkles size={14} /> Le droit expliqué par l'IA
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-navy mb-6">
              Comprenez vos droits.<br />En toute simplicité.
            </h1>
            <p className="text-base leading-relaxed text-slate-600 max-w-xl mb-8">
              99% des citoyens ne peuvent pas se permettre un premier conseil juridique. Droits IA.com rend le droit français accessible : posez vos questions, analysez vos documents, et préparez sereinement votre rendez-vous avocat.
            </p>
            <div className="flex flex-wrap gap-4">
              <button data-testid="hero-cta-btn" onClick={handleLogin} className="bg-navy hover:bg-[#172A66] text-white px-7 py-3.5 rounded-md font-medium flex items-center gap-2 transition-all duration-200">
                Commencer gratuitement <ArrowRight size={18} />
              </button>
              <button onClick={handleLogin} className="border border-navy/20 hover:border-navy text-navy px-7 py-3.5 rounded-md font-medium transition-all duration-200">
                Se connecter
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-4">Connexion sécurisée via Google · Conforme RGPD</p>
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="bg-white border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4 text-royal">
                <Scale size={18} /><span className="text-sm font-semibold">Exemple d'analyse</span>
              </div>
              <div className="legal-memo text-sm">
                <h2>📋 Résumé</h2>
                <p>Vous avez reçu une convocation civile pour comparaître le 15 septembre.</p>
                <h2>✅ Vos droits</h2>
                <p>Droit à la défense, droit à un délai de préparation, accès à votre dossier.</p>
                <blockquote>💡 Cette analyse est informative et ne remplace pas un conseil juridique.</blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <h2 className="font-serif text-3xl lg:text-4xl text-navy tracking-tight mb-3">Un assistant de première ligne</h2>
        <p className="text-slate-500 max-w-2xl mb-14">Professionnel, transparent et pédagogue. Chaque réponse cite ses sources et rappelle ses limites.</p>
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((f) => (
            <div key={f.title} className="bg-white border border-border rounded-lg p-8 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
              <div className="h-11 w-11 rounded-md bg-navy/5 flex items-center justify-center text-royal mb-5">
                <f.icon size={22} />
              </div>
              <h3 className="font-serif text-2xl text-navy mb-2">{f.title}</h3>
              <p className="text-slate-600 leading-relaxed text-[15px]">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="bg-navy rounded-2xl px-10 py-16 text-center relative overflow-hidden">
          <div className="relative">
            <h2 className="font-serif text-3xl lg:text-4xl text-white mb-4">Prêt à y voir plus clair ?</h2>
            <p className="text-white/60 max-w-lg mx-auto mb-8">Créez votre compte en quelques secondes et posez votre première question juridique.</p>
            <button data-testid="footer-cta-btn" onClick={handleLogin} className="bg-white text-navy hover:bg-gold hover:text-navy px-8 py-3.5 rounded-md font-medium inline-flex items-center gap-2 transition-all duration-200">
              Commencer maintenant <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <p>© 2026 Droits IA.com · Le droit expliqué par l'intelligence artificielle</p>
          <p>Informatif uniquement · Ne remplace pas un avocat</p>
        </div>
      </footer>
    </div>
  );
}
